import { useState } from "react"

import { useTranslation } from "react-i18next"

import { AlertCircle, AlertTriangle, Check, Info, Send } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { STATUS_TONES } from "@/components/ui/tone-classes"

import { useSendTestAlertMutation } from "../../hooks/admin-queries"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AlertsTestDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const [severity, setSeverity] = useState<"info" | "warning" | "critical">("info")
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  const sendMutation = useSendTestAlertMutation()

  const handleSend = async () => {
    setResult(null)
    try {
      const response = await sendMutation.mutateAsync({ severity })
      let modeIndicator = ""
      if (response.delivery_mode === "dry_run") {
        modeIndicator = " (dry run)"
      } else if (response.delivery_mode === "log_only") {
        modeIndicator = " (log only)"
      }
      setResult({
        success: response.success,
        message: response.message + modeIndicator,
      })
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Failed to send test alert",
      })
    }
  }

  const handleClose = () => {
    setResult(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="size-5" />
            {t("adminDashboard.alerts.sendTestTitle", "Send Test Alert")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.alerts.sendTestDesc",
              "Send a test alert to verify your configuration is working correctly."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t("adminDashboard.alerts.selectSeverity", "Select Severity")}</Label>
            <RadioGroup
              value={severity}
              onValueChange={(value) => setSeverity(value as "info" | "warning" | "critical")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem id="info" value="info" />
                <Label className="flex cursor-pointer items-center gap-2" htmlFor="info">
                  <Info className="text-primary size-4" />
                  Info
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem id="warning" value="warning" />
                <Label className="flex cursor-pointer items-center gap-2" htmlFor="warning">
                  <AlertTriangle className="text-warning size-4" />
                  Warning
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem id="critical" value="critical" />
                <Label className="flex cursor-pointer items-center gap-2" htmlFor="critical">
                  <AlertCircle className="text-destructive size-4" />
                  Critical
                </Label>
              </div>
            </RadioGroup>
          </div>

          {result && (
            <div
              className={`flex items-center gap-2 rounded-lg p-3 ${
                result.success ? STATUS_TONES.success.soft : STATUS_TONES.destructive.soft
              }`}
            >
              {result.success ? <Check className="size-5" /> : <AlertCircle className="size-5" />}
              {result.message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={sendMutation.isPending} onClick={handleSend}>
            {sendMutation.isPending ? (
              t("common.sending", "Sending...")
            ) : (
              <>
                <Send className="mr-2 size-4" />
                {t("adminDashboard.alerts.sendTest", "Send Test Alert")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
